import { Fragment, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useUrlString, useUrlPartialRangeOrNull, useUrlNumber } from '../../hooks/useUrlState';
import { ChevronRight, ChevronDown, Truck, PackageCheck, Send, FileText, Search, Download, BadgeCheck, XCircle, MapPin, X, Layers } from 'lucide-react';
import { request } from '../../api/client';
import { useDebounce } from '../../hooks/useDebounce';
import { useServerSort, type SortState } from '../../hooks/useServerSort';
import { useAuthStore, useCanSeeWarehouses } from '../../store/auth';
import { Pill } from '../../components/TwDataTable';
import { ExportDialog } from '../../components/ExportDialog';
import { Select, Input, StatCard, statTone, Button, FilterResetButton, CodeChip, entityHue, initialsOf, DatePicker, Pagination } from '../../ui';
import { customerOption, CUSTOMER_SELECT_PROPS } from '../../components/CustomerOption';
import { disableFuture } from '../../lib/dateRange';
import { formatIst } from '../../lib/time';
import { warehouseOption } from '../../lib/warehouseLabel';
import { accentForProductName } from '../../lib/productAccent';
import { AttachmentCell } from '../../components/AttachmentPreview';

const qf = (n: any) => Number(n ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 3 });
/** Must match `NO_CARRIER` in the backend's shipment.service — the "no carrier recorded" filter slice. */
const NO_CARRIER = '__NONE__';
const plural = (n: number, one: string, many = `${one}s`) => `${qf(n)} ${n === 1 ? one : many}`;
/**
 * What the shipment CONTAINS, in the two counts that are not the same number.
 *
 * Picking one product out of two batches writes two `shipment_items` rows, and this line used to read
 * "across 2 items" off that row count — telling the reader a second product was in the box when the
 * box held one product split across two batches. Products is what a person means by "items"; the batch
 * count is only worth printing when it differs, because "1 product · 1 batch" says nothing twice.
 */
const contentsLine = (r: any) => {
  const products = Number(r.itemCount ?? 0);
  const batches = Number(r.batchCount ?? products);
  return batches !== products
    ? `${plural(products, 'product')} · ${plural(batches, 'batch', 'batches')}`
    : plural(products, 'product');
};
/**
 * One entry per PRODUCT, carrying the batches it was picked from.
 *
 * The server sends a row per picked batch because that is what a shipment line is; a reader looking at
 * a packing list wants the product once, with its total, and the batch split underneath it.
 */
type ItemGroup = { key: string; productName: string; qty: number; batches: any[] };
const groupByProduct = (items: any[]): ItemGroup[] => {
  const out: ItemGroup[] = [];
  const byKey = new Map<string, ItemGroup>();
  for (const [i, it] of (items ?? []).entries()) {
    // Group by product id; a legacy line with no id falls back to its own row rather than merging
    // every unidentified line into one heap.
    const key = it.productId ? `p:${it.productId}` : `i:${i}`;
    let g = byKey.get(key);
    if (!g) { g = { key, productName: it.productName || '—', qty: 0, batches: [] }; byKey.set(key, g); out.push(g); }
    g.qty += Number(it.qty) || 0;
    g.batches.push(it);
  }
  return out;
};
const dt = (v: any) => (v ? formatIst(v) : null);
const dateOnly = (v: any) => (v ? String(v).substring(0, 10) : null);
// "2026-09-10" is how a database writes a date. Every other register in the app writes "10 Sep 2026",
// and a dispatch register is read by people judging how long ago something left.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtDate = (v: any) => {
  const d = dateOnly(v);
  if (!d) return null;
  const [y, m, dd] = d.split('-');
  return MONTHS[Number(m) - 1] ? `${dd} ${MONTHS[Number(m) - 1]} ${y}` : d;
};

/** A hue worn as a surface — wash, hairline ring, glyph — from one colour value. Inline styles, so
 *  real colour values: Tailwind only emits the theme vars a CLASS references. */
const hueSkin = (hue: string, wash = 14, ring = 38) => ({
  background: `color-mix(in srgb, ${hue} ${wash}%, transparent)`,
  boxShadow: `inset 0 0 0 1px color-mix(in srgb, ${hue} ${ring}%, transparent)`,
  color: hue,
});

/**
 * Shipment status → label, pill colour, and the two things that let a stage be read before any text
 * is: an icon and the hue of the row's left accent bar. "Packed" reads as "ready to leave" to a
 * warehouse user, which is why the label is not the raw status.
 */
const STATUS_META: Record<string, { label: string; color: string; Icon: typeof Truck; tile: string; bar: string; rail: string; wash: string }> = {
  PACKED: { label: 'Packed', color: 'gold', Icon: PackageCheck, tile: 'bg-harvest-400/20 text-harvest-700 dark:text-harvest-300', bar: 'bg-harvest-400', rail: 'border-l-harvest-400', wash: 'from-harvest-400/[0.08]' },
  DISPATCHED: { label: 'In transit', color: 'blue', Icon: Truck, tile: 'bg-sky-500/12 text-sky-600 dark:text-sky-400', bar: 'bg-sky-500', rail: 'border-l-sky-500', wash: 'from-sky-500/[0.07]' },
  DELIVERED: { label: 'Delivered', color: 'green', Icon: BadgeCheck, tile: 'bg-brand-500/12 text-brand-700 dark:text-brand-300', bar: 'bg-brand-500', rail: 'border-l-brand-500', wash: 'from-brand-500/[0.07]' },
  CANCELLED: { label: 'Cancelled', color: 'red', Icon: XCircle, tile: 'bg-danger-500/12 text-danger-600 dark:text-danger-500', bar: 'bg-danger-500', rail: 'border-l-danger-500', wash: 'from-danger-500/[0.07]' },
};
const FALLBACK_META = { color: 'default', Icon: Truck, tile: 'bg-surface-3 text-muted', bar: 'bg-line-strong', rail: 'border-l-line', wash: 'from-transparent' };

/**
 * Dispatch register — outbound shipments from the warehouse(s). Shipment-level (a partial dispatch is its own
 * row); each row shows its current status, and expands to the full lifecycle (packed → dispatched → delivered),
 * logistics, the itemised lines with batches, and the linked invoice / challan. A Warehouse Manager sees only
 * their warehouse(s); Admin / Super Admin see all warehouses and can narrow with the warehouse filter.
 */
export function DispatchesPage() {
  const canSeeWarehouses = useCanSeeWarehouses();
  const role = useAuthStore((s) => s.user?.role);
  const isWHM = role === 'WAREHOUSE_MANAGER';

  const [status, setStatus] = useUrlString('status', 'ALL');
  const [warehouseId, setWarehouseId] = useUrlString('wh', 'ALL');
  const [retailerId, setRetailerId] = useUrlString('retailer', 'ALL');
  const [carrier, setCarrier] = useUrlString('carrier', 'ALL');
  // The RangePicker emits [start, null] on the first click, so the endpoints are held nullable and the
  // query only carries a bound once it is actually set — `range[1].format()` on a half-picked range crashes.
  const [range, setRange] = useUrlPartialRangeOrNull('period');
  // The "dispatched today" drill. Its own filter, not a from/to: that pair keys off
  // COALESCE(dispatched_at, created_at) and would also match whatever was merely PACKED that day.
  const [dispatchedOn, setDispatchedOn] = useUrlString('on', '');
  const [searchInput, setSearchInput] = useUrlString('q');
  const search = useDebounce(searchInput, 400);
  // TRUE server-side paging — the page number is part of the address, so a row found on page 4 is
  // still on page 4 after Back.
  const [page, setPage] = useUrlNumber('page', 1);
  const [pageSize, setPageSize] = useUrlNumber('pageSize', 25);
  const [open, setOpen] = useState<Set<string>>(new Set());
  /* CHANGING A FILTER RETURNS YOU TO PAGE 1. Staying on page 7 of a set that a filter just cut to
     twelve rows shows an empty table with no explanation; the server clamps too (so a bookmarked
     ?page=9 lands on the last real page), but the URL should say what you are actually looking at. */
  const onFilter = <T,>(set: (v: T) => void) => (v: T) => { set(v); setPage(1); };
  // Same gate as the page and the endpoint: the register is a movement record with no pricing, and the
  // Warehouse Manager (WAREHOUSE.VIEW.ASSIGNED) is its main reader.
  const [exportOpen, setExportOpen] = useState(false);
  const toggle = (id: string) => setOpen((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  // Server-side column sort (backend ORDER BY over the whole result set, not just the 500-row display window).
  const { sortState, onSortChange: onSortChangeRaw, sort } = useServerSort();
  // A new sort reorders the whole result set, so page 4 of the old order is not page 4 of the new one.
  const onSortChange = (next: any) => { onSortChangeRaw(next); setPage(1); };

  const params = useMemo(() => {
    const p: Record<string, any> = { page, pageSize };
    if (status !== 'ALL') p.status = status;
    if (warehouseId !== 'ALL') p.warehouseId = warehouseId;
    if (retailerId !== 'ALL') p.retailerId = retailerId;
    if (carrier !== 'ALL') p.carrier = carrier;
    if (range?.[0]) p.from = range[0].format('YYYY-MM-DD');
    if (range?.[1]) p.to = range[1].format('YYYY-MM-DD');
    if (dispatchedOn) p.dispatchedOn = dispatchedOn;
    if (search.trim()) p.search = search.trim();
    if (sort) p.sort = sort;
    return p;
  }, [status, warehouseId, retailerId, carrier, range, dispatchedOn, search, sort, page, pageSize]);

  const { data, isLoading } = useQuery({
    queryKey: ['dispatches', params],
    queryFn: () => request<any>({ url: '/dispatches', params }),
  });
  const rows: any[] = data?.rows ?? [];
  const s = data?.summary ?? { readyToDispatch: 0, inTransit: 0, delivered: 0, cancelled: 0, dispatchedToday: 0, total: 0, today: null };
  // The retailer and carrier lists come from the REGISTER's own scope, not from the masters: every option
  // then returns rows, and a Warehouse Manager — who has no RETAILER.VIEW — still gets a dealer picker.
  const facets = data?.facets ?? { retailers: [], carriers: [], noCarrier: 0 };
  // Counted server-side over the FILTERED set, so "of N" is the real answer rather than the size of
  // the slice that happened to arrive.
  const total: number = Number(data?.total ?? 0);
  const current: number = Number(data?.page ?? page);
  /* `narrowed` drives the amber GAP LINE only: it asks "do the tiles disagree with the list?", and the
     warehouse is not one of those filters — the KPIs are computed INSIDE the chosen warehouse, so tile
     and rows still match and there is no gap to confess to.
     `anyFilter` asks the plainer question the BUTTON asks — "is anything set?" — and counts the
     warehouse, because a warehouse sitting in the picker is plainly a filter. */
  const narrowed = status !== 'ALL' || retailerId !== 'ALL' || carrier !== 'ALL' || !!range || !!dispatchedOn || !!search.trim();
  const anyFilter = narrowed || warehouseId !== 'ALL';
  /* ONE "Clear filters" MEANING. Both places that offer it — the toolbar button and the amber gap
     line — run THIS, and clear everything including the warehouse. Two controls sharing a label and
     quietly doing different things (one keeping the warehouse, one not) is worse than either rule on
     its own: the reader presses the nearer one, sees a filter survive, and rightly calls it broken.
     The warehouse-preserving variant below is reachable only by clicking a KPI tile, where keeping it
     is the point — the tile's number was counted inside that warehouse.
     Every setter writes through the hook's shared coalescing buffer, so all of them survive into one
     history entry rather than the last call winning. */
  const clearAll = () => {
    setStatus('ALL'); setRetailerId('ALL'); setCarrier('ALL'); setRange(null); setDispatchedOn('');
    setSearchInput(''); setWarehouseId('ALL'); setPage(1);
  };
  // A tile keeps the warehouse (its number was computed inside it) but drops every other filter.
  const clearNarrowing = () => {
    setStatus('ALL'); setRetailerId('ALL'); setCarrier('ALL'); setRange(null); setDispatchedOn('');
    setSearchInput(''); setPage(1);
  };
  /* A tile opens its WHOLE slice. It counts the warehouse's entire pipeline, so it has to drop every other
     narrowing filter on the way in — leaving a search box or a date range standing would show "Delivered 31"
     over four rows, which is the exact mismatch these tiles are supposed to stop making. The warehouse stays,
     because the number was computed inside it. */
  const drillStatus = (v: string) => {
    const wasOnlyThis = status === v && !dispatchedOn;
    clearNarrowing();
    if (!wasOnlyThis) setStatus(v);
  };
  const drillDispatchedOn = () => {
    const day = dispatchedOn ? '' : (s.today ?? '');
    clearNarrowing();
    if (day) setDispatchedOn(day);
  };

  const { data: warehouses } = useQuery({
    queryKey: ['warehouses-all'],
    queryFn: () => request<any[]>({ url: '/warehouses/my' }),
    // Gated so a role without warehouse access doesn't fire a call it is refused — see useCanSeeWarehouses.
    enabled: canSeeWarehouses && !isWHM,
  });

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h1 className="type-page-title text-content">Dispatches</h1>
        <Button size="sm" className="ml-auto" icon={<Download size={16} />} onClick={() => setExportOpen(true)}>Export</Button>
      </div>

      {/* KPIs — the warehouse's outbound pipeline, and every one of them opens the rows behind it.
          THE FOUR STAGE TILES ARE A PARTITION: packed + in transit + delivered + cancelled is every
          shipment this warehouse has, so they are readable as shares of one whole and "Cancelled" is
          no longer the 28 shipments nobody could see. "Dispatched today" is deliberately last and
          reads on a different basis — a DAY's activity, overlapping the two tiles to its left rather
          than partitioning anything — which is why its hint names the date it counted.
          The numbers are computed from the warehouse SCOPE only, never from the filtered list, so the
          band keeps reading as the whole pipeline while the table below is narrowed; the gap line
          under it says so out loud whenever the two disagree. */}
      <div className="mb-3 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
        <Kpi title="Ready to dispatch" value={s.readyToDispatch} accent="border-l-warning-500" icon={<PackageCheck size={16} />}
          hint={status === 'PACKED' && !dispatchedOn ? 'Showing these — click to clear' : 'Packed, still in the warehouse'}
          active={status === 'PACKED' && !dispatchedOn} onClick={() => drillStatus('PACKED')} />
        <Kpi title="In transit" value={s.inTransit} accent="border-l-sky-500" icon={<Truck size={16} />}
          hint={status === 'DISPATCHED' && !dispatchedOn ? 'Showing these — click to clear' : 'Left, not yet delivered'}
          active={status === 'DISPATCHED' && !dispatchedOn} onClick={() => drillStatus('DISPATCHED')} />
        <Kpi title="Delivered" value={s.delivered} accent="border-l-success-500" icon={<BadgeCheck size={16} />}
          hint={status === 'DELIVERED' && !dispatchedOn ? 'Showing these — click to clear' : 'All time'}
          active={status === 'DELIVERED' && !dispatchedOn} onClick={() => drillStatus('DELIVERED')} />
        <Kpi title="Cancelled" value={s.cancelled} accent="border-l-danger-500" icon={<XCircle size={16} />}
          hint={status === 'CANCELLED' && !dispatchedOn ? 'Showing these — click to clear' : 'All time'}
          active={status === 'CANCELLED' && !dispatchedOn} onClick={() => drillStatus('CANCELLED')} />
        <Kpi title="Dispatched today" value={s.dispatchedToday} accent="border-l-brand-500" icon={<Send size={16} />}
          hint={dispatchedOn ? 'Showing these — click to clear' : `Left on ${fmtDate(s.today) ?? 'today'}`}
          active={!!dispatchedOn} onClick={drillDispatchedOn} />
      </div>

      {/* The tiles count the warehouse; the table counts what the filters left. When those differ, say
          by how much and offer the way back — a KPI that silently outruns the list beneath it is the
          bug this page was reported for. */}
      {narrowed && !isLoading && (
        <div className="mb-3 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-card border border-warning-500/30 bg-warning-500/8 px-3 py-2 type-label text-warning-700 dark:text-harvest-300">
          <span>
            Tiles count all {qf(s.total)} shipment{s.total === 1 ? '' : 's'} in this warehouse view — your filters
            narrow the list below to {qf(total)}.
          </span>
          <button type="button" onClick={clearAll} className="font-semibold underline underline-offset-2 hover:opacity-80">Clear filters</button>
        </div>
      )}

      {/* FILTERS — one control per column the table draws, in the table's own left-to-right order:
          Shipment/LR → Date → Retailer → Warehouse → Carrier → Status. A register whose server accepts
          a filter the toolbar never offers is a register people export and then re-filter in Excel, and
          every one of these already existed on the endpoint. The same set is what the Export dialog
          sends, so the file and the screen can never disagree. */}
      <div className="mb-3 flex flex-wrap items-center gap-2 rounded-card border border-line bg-surface shadow-e1 px-3 py-2.5">
        <div className="relative">
          <Search size={16} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" />
          <Input value={searchInput} onChange={(e) => { setSearchInput(e.target.value); setPage(1); }} placeholder="Shipment / order / LR / vehicle…" size="sm" className="w-64 pl-8" />
        </div>
        <DatePicker.RangePicker
          size="small" allowClear allowPartial disabledDate={disableFuture}
          value={range as any} onChange={(v: any) => { setRange(v ?? null); setPage(1); }}
        />
        <Select
          value={retailerId} onChange={onFilter(setRetailerId)} size="sm" className="w-56" {...CUSTOMER_SELECT_PROPS}
          options={[
            { value: 'ALL', title: 'All retailers', label: 'All retailers' },
            // `territoryId` is passed through so the option draws the whole address chain rather
            // than the town on its own — customerOption prefers the node over a bare name.
            ...(facets.retailers ?? []).map((r: any) => customerOption({ id: r.id, companyName: r.name, code: r.code, city: r.city, territoryId: r.territoryId, territoryName: r.territoryName })),
          ]}
        />
        {!isWHM && (
          <Select
            value={warehouseId} onChange={onFilter(setWarehouseId)} size="sm" className="w-56"
            options={[{ value: 'ALL', label: 'All warehouses' }, ...(warehouses ?? []).map((w: any) => ({ value: String(w.id), ...warehouseOption(w) }))]}
          />
        )}
        <Select
          value={carrier} onChange={onFilter(setCarrier)} size="sm" className="w-52" showSearch optionFilterProp="title"
          options={[
            { value: 'ALL', title: 'All carriers', label: 'All carriers' },
            // The amber "no carrier recorded" rows are a slice people actually chase, so they get an
            // option rather than being reachable only by scrolling for the gaps.
            ...(facets.noCarrier > 0 ? [{ value: NO_CARRIER, title: 'No carrier recorded', label: `No carrier recorded (${facets.noCarrier})` }] : []),
            ...(facets.carriers ?? []).map((c: any) => ({ value: c.name, title: c.name, label: `${c.name} (${c.shipments})` })),
          ]}
        />
        <Select
          value={status} onChange={onFilter(setStatus)} size="sm" className="w-44"
          options={[
            { value: 'ALL', label: 'All statuses' },
            { value: 'PACKED', label: 'Packed (ready)' },
            { value: 'DISPATCHED', label: 'In transit' },
            { value: 'DELIVERED', label: 'Delivered' },
            { value: 'CANCELLED', label: 'Cancelled' },
          ]}
        />
        {/* The KPI drill has no dropdown of its own, so it needs a visible, removable token — otherwise
            a tile-filtered list looks unfiltered and the missing rows read as a bug. */}
        {dispatchedOn && (
          <span className="inline-flex items-center gap-1.5 rounded-pill border border-brand-500/40 bg-brand-500/10 px-2.5 py-1 type-label text-brand-700 dark:text-brand-300">
            Dispatched on {fmtDate(dispatchedOn)}
            <button type="button" onClick={() => { setDispatchedOn(''); setPage(1); }} aria-label="Clear the dispatched-on filter" className="opacity-70 hover:opacity-100">
              <X size={13} />
            </button>
          </span>
        )}
        {anyFilter && (
          <FilterResetButton onClick={clearAll} className="ml-auto" />
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-card border border-line bg-surface shadow-e1">
        <table className="w-full min-w-[860px] text-sm">
          <thead className="gb-thead-tint">
            <tr className="border-b border-line text-left text-2xs uppercase tracking-wide text-muted">
              <th className="w-8 px-2 py-2.5"></th>
              <SortTh label="Shipment" sortKey="shipmentNo" sortState={sortState} onSortChange={onSortChange} />
              <SortTh label="Date" sortKey="date" sortState={sortState} onSortChange={onSortChange} />
              <SortTh label="Order · Retailer" sortKey="orderNo" sortState={sortState} onSortChange={onSortChange} />
              {!isWHM && <th className="px-3 py-2.5">Warehouse</th>}
              <th className="px-3 py-2.5 text-right">Total qty</th>
              {/* A right-aligned figure meeting left-aligned text needs more than the default 12px a
                  side, or "100 / 1 item" reads as one string with the carrier beside it. */}
              <th className="py-2.5 pl-8 pr-3">Carrier / vehicle</th>
              <SortTh label="Status" sortKey="status" sortState={sortState} onSortChange={onSortChange} />
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={isWHM ? 7 : 8} className="px-3 py-10 text-center text-muted">Loading…</td></tr>}
            {!isLoading && !rows.length && <tr><td colSpan={isWHM ? 7 : 8} className="px-3 py-10 text-center text-muted">No dispatches match these filters.</td></tr>}
            {rows.map((r) => {
              const isOpen = open.has(String(r.id));
              const meta = STATUS_META[r.status] ?? { ...FALLBACK_META, label: r.status };
              return (
                <ExpandableRow key={r.id} r={r} isOpen={isOpen} onToggle={() => toggle(String(r.id))} meta={meta} isWHM={isWHM} />
              );
            })}
          </tbody>
        </table>
      </div>
      {/* The pager REPLACES a flat "showing the 500 most recent shipments" footnote. That was not paging:
          row 501 was unreachable by anything except narrowing the filters until it fell inside the cap. */}
      {total > 0 && (
        <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
          <Pagination
            size="small" current={current} pageSize={pageSize} total={total}
            showSizeChanger pageSizeOptions={[10, 25, 50, 100]}
            onChange={(p, ps) => { setPage(ps === pageSize ? p : 1); setPageSize(ps); }}
            showTotal={(t, r) => `${r[0]}–${r[1]} of ${qf(t)} shipment${t === 1 ? '' : 's'}`}
          />
        </div>
      )}
      {/* Period picker shown: dispatches ARE a date-ranged movement log, unlike the snapshot registers
          (catalogue / ageing / valuation), where a range would filter nothing. */}
      <ExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        title="Export dispatch register"
        description="Outbound shipments: order, retailer, warehouse and status. Excel adds carrier, LR, vehicle and driver."
        baseUrl="/dispatches/export"
        /* Paging is a way of LOOKING at the register, not a filter on it: a file that held page 3 and
           nothing else would be a quiet lie, so page/pageSize are stripped and the server exports every
           row the filters matched. */
        /* `sort` too: the column the reader sorted the register by is part of the view the
           file claims to be. Stripping it handed back a file in some other order. */
        baseParams={(() => { const q = { ...params }; delete q.page; delete q.pageSize; return q; })()}
        /* The dialog's own period picker is gone: this page now filters by date itself, and two places to
           set one range is two ways to disagree about what the file holds. With it hidden the page's
           from/to travel through baseParams untouched, so the export IS the screen. */
        hidePeriod
      />
    </div>
  );
}

// Clickable, server-sorted column header for the hand-rolled dispatch table. Click cycles asc → desc → off,
// exactly like the shared TwDataTable header, and reports the change up via useServerSort's onSortChange.
function SortTh({ label, sortKey, sortState, onSortChange, align }: {
  label: string; sortKey: string; sortState: SortState;
  onSortChange: (next: SortState) => void; align?: 'right';
}) {
  const dir = sortState?.key === sortKey ? sortState.dir : null;
  const onClick = () => onSortChange(dir === null ? { key: sortKey, dir: 'asc' } : dir === 'asc' ? { key: sortKey, dir: 'desc' } : null);
  return (
    <th className={`cursor-pointer select-none px-3 py-2.5 hover:text-content ${align === 'right' ? 'text-right' : ''}`} onClick={onClick}>
      <span className={`inline-flex items-center ${align === 'right' ? 'flex-row-reverse' : ''}`}>
        {label}
        <span className="ml-1 inline-flex flex-col leading-[0]">
          <svg viewBox="0 0 8 5" className={`h-[5px] w-2 ${dir === 'asc' ? 'text-brand-600' : 'text-line-strong'}`} fill="currentColor"><path d="M4 0l4 5H0z" /></svg>
          <svg viewBox="0 0 8 5" className={`mt-[2px] h-[5px] w-2 ${dir === 'desc' ? 'text-brand-600' : 'text-line-strong'}`} fill="currentColor"><path d="M4 5L0 0h8z" /></svg>
        </span>
      </span>
    </th>
  );
}

type StatusMeta = { label: string; color: string; Icon: typeof Truck; tile: string; bar: string; rail: string; wash: string };

function ExpandableRow({ r, isOpen, onToggle, meta, isWHM }: { r: any; isOpen: boolean; onToggle: () => void; meta: StatusMeta; isWHM: boolean }) {
  const cols = isWHM ? 7 : 8;
  return (
    <>
      <tr className={`cursor-pointer border-b border-line/70 ${isOpen ? `bg-linear-to-r ${meta.wash} to-transparent to-55%` : 'hover:bg-surface-2'}`} onClick={onToggle}>
        {/* The accent bar rides the chevron cell: the shipment's stage is the first thing the row
            says, before a single word is read. */}
        <td className="relative px-2 py-2.5 pl-3 text-muted">
          <span className={`absolute inset-y-0 left-0 w-1 ${meta.bar}`} aria-hidden />
          {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </td>
        <td className="px-3 py-2.5">
          <span className="flex min-w-0 items-center gap-2">
            <span className={`grid size-7 shrink-0 place-items-center rounded-control ${meta.tile}`}>
              <meta.Icon size={15} />
            </span>
            <span className="truncate font-medium text-brand-700 dark:text-brand-400">{r.shipmentNo}</span>
          </span>
        </td>
        <td className="whitespace-nowrap px-3 py-2.5 text-muted">{fmtDate(r.dispatchedAt) ?? fmtDate(r.packedAt) ?? '—'}</td>
        <td className="px-3 py-2.5">
          {/* No mark here. It was an EntityAvatar carrying a CONSTANT shop glyph — the same picture on
              every row, which is decoration, not identity (see the row-mark rule). The order number
              and the dealer beneath it already tell the rows apart. */}
          <span className="block min-w-0">
            <div className="truncate font-medium text-content">{r.orderNo ?? `#${r.orderId}`}</div>
            {/* Code · name · city was one grey run-on string in which the code — the half people scan
                for — dissolved. The code gets its own shape, the city its own pin. */}
            <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-1.5">
              {r.retailerCode && <CodeChip hue={entityHue(r.retailerCode)}>{r.retailerCode}</CodeChip>}
              <span className="truncate type-label text-muted">{r.retailerName ?? `#${r.retailerId}`}</span>
              {r.retailerCity && (
                <span className="inline-flex shrink-0 items-center gap-0.5 type-caption text-subtle">
                  <MapPin size={10} className="shrink-0" />{r.retailerCity}
                </span>
              )}
            </div>
          </span>
        </td>
        {!isWHM && (
          <td className="px-3 py-2.5">
            {/* The dispatching warehouse in its own identity colour — the same hue it wears in the
                picker above, in the stock table, and beside every avatar app-wide. */}
            {r.warehouseCode
              ? <CodeChip hue={entityHue(r.warehouseCode)} title={r.warehouseName}>{r.warehouseCode}</CodeChip>
              : r.warehouseName ? <span className="block truncate text-content">{r.warehouseName}</span> : <span className="text-subtle">—</span>}
          </td>
        )}
        <td className="whitespace-nowrap px-3 py-2.5 text-right">
          {/* "12" stacked over "2 items" is two bare numbers with only one of them labelled, so the
              reader has to guess which is the quantity. The total keeps the metric weight (and its
              tabular figures, so the column's digits line up); the line under it NAMES what its own
              number counts. */}
          <div className="type-metric-sm text-content">{qf(r.totalQty)}</div>
          <div className="type-label text-subtle">{contentsLine(r)}</div>
        </td>
        <td className="py-2.5 pl-8 pr-3">
          {/* Every carrier chip was the same grey, so a column of them said only "there is a carrier".
              The hue is the transporter's own identity colour, so one carrier's shipments group by eye
              down the column; the vehicle stays a separate plate rather than dot-joined to the name. */}
          {r.carrier || r.vehicleNo ? (
            <span className="inline-flex min-w-0 items-center gap-1.5">
              {r.carrier && (
                <span className="inline-flex min-w-0 items-center gap-1 rounded-pill px-2 py-0.5 type-label leading-5" style={hueSkin(entityHue(r.carrier), 13, 34)}>
                  <Truck size={11} className="shrink-0" />
                  <span className="truncate">{r.carrier}</span>
                </span>
              )}
              {r.vehicleNo && <CodeChip>{r.vehicleNo}</CodeChip>}
            </span>
          ) : <span className="type-label text-warning-600 dark:text-harvest-300">no carrier recorded</span>}
        </td>
        <td className="px-3 py-2.5"><Pill color={meta.color}>{meta.label}</Pill></td>
      </tr>
      {isOpen && (
        <tr className="border-y border-line bg-surface-2">
          <td colSpan={cols} className="p-0">
            <div className={`border-l-[3px] ${meta.rail} bg-linear-to-r ${meta.wash} to-transparent to-45% px-5 py-4`}>
              <div className="grid gap-x-6 gap-y-4 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1.15fr)_186px]">
                {/* Shipment details */}
                <div>
                  <ColHead>Shipment details</ColHead>
                  <DetailGrid rows={[
                    [{ label: 'Warehouse', value: r.warehouseCode ? <span className="flex min-w-0 items-center gap-1.5"><CodeChip hue={entityHue(r.warehouseCode)} className="shrink-0">{r.warehouseCode}</CodeChip><span className="min-w-0 truncate text-muted">{r.warehouseName}</span></span> : (r.warehouseName || null) }, { label: 'Total qty', value: `${qf(r.totalQty)} · ${contentsLine(r)}` }],
                    [{ label: 'Carrier', value: r.carrier }, { label: 'Vehicle', value: r.vehicleNo }],
                    [{ label: 'LR / docket', value: r.lrNo }, { label: 'Driver', value: r.driverName ? `${r.driverName}${r.driverMobile ? ` · ${r.driverMobile}` : ''}` : null }],
                    /* The signed delivery note, SHOWN. "View proof →" sent the reader to a new tab to find out
                        whether the photo was legible — the same question the thumbnail answers in place. */
                    [{ label: 'Expected', value: dt(r.expectedAt) }, { label: 'POD', value: r.podUrl ? <span onClick={(e) => e.stopPropagation()}><AttachmentCell files={[r.podUrl]} label="Proof of delivery" size={36} /></span> : null }],
                  ]} />
                </div>

                {/* Items */}
                <ShipmentItems r={r} />

                {/* Status timeline */}
                <div>
                  <ColHead>Status timeline</ColHead>
                  <VerticalTimeline r={r} />
                </div>
              </div>

              {/* Linked documents */}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {r.invoiceNo && (
                  <DocChip icon={<FileText size={14} />} label="Invoice" value={r.invoiceNo} to={r.invoiceId ? `/billing/invoices/${r.invoiceId}` : undefined} warn={r.verificationStatus && r.verificationStatus !== 'VERIFIED' ? String(r.verificationStatus).toLowerCase() : undefined} />
                )}
                {r.challanNo && <DocChip icon={<FileText size={14} />} label="Challan" value={r.challanNo} />}
                <Link to={`/orders/${r.orderId}`} onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 rounded-control border border-line bg-surface px-3.5 py-1.5 type-body-medium text-content transition-colors hover:bg-surface-2">Open order →</Link>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * WHAT IS IN THE BOX — a product at a time, not a pick line at a time.
 *
 * The flat version printed one row per `shipment_item`, so a product picked from two batches appeared
 * twice under the same name with the quantity split between the copies, and the header called that "2
 * items". Nothing on screen said the two lines were the same product, so the honest readings were "the
 * order was entered twice" or "the page is broken" — and the qty a person actually wants (how much of
 * this product is going) was nowhere on the panel; it had to be added up by eye.
 *
 * So the product is the row, and it carries its OWN total. Where a product really was picked from more
 * than one batch, those batches are indented beneath it as what they are — a split of that total —
 * each with its expiry and its box/loose breakdown.
 *
 * BUT A SINGLE BATCH IS NOT A SPLIT. Nesting it wrote the SAME QUANTITY on two consecutive lines —
 * "ZZ-E2E Test Bio Tonic … 75" and under it "ZZ-CLEAN … 75" — which is a number printed twice on one
 * screen, and a reader meeting it reads either a mistake or two different facts. An earlier revision
 * argued for nesting always, on the grounds that one layout beating two is worth the repetition; that
 * is overruled. The batch is a COLUMN, so a one-batch product is one line that carries its own batch,
 * and the indent appears only when there is genuinely something to indent.
 */
/** A picked lot: its number, and the expiry that is the reason anyone reads a batch number at all.
 *  A line with no batch on file says so in amber rather than leaving the cell blank. */
function BatchCell({ b }: { b: any }) {
  if (!b?.batchNo) return <span className="type-label text-warning-600 dark:text-harvest-300">no batch recorded</span>;
  return (
    /* NOWRAP, and the column below is sized to hold both. The expiry belongs BESIDE the batch number
       because it is a fact ABOUT that number — which lot, and how long it is good for, read as one
       phrase. Left free to wrap it dropped under the chip whenever the pair outgrew the column, and a
       date sitting on its own line under a code reads as a separate field about the row, not about the
       lot. If a batch number is long enough to outgrow the hint the column widens; it never stacks. */
    <span className="inline-flex min-w-0 flex-nowrap items-center gap-x-1.5 whitespace-nowrap">
      <CodeChip>{b.batchNo}</CodeChip>
      {b.expiry ? <span className="shrink-0 type-caption text-subtle">exp {fmtDate(b.expiry)}</span> : null}
    </span>
  );
}

/** How a line's quantity is made up in boxes, when the product is box-packed and the split is known. */
const packText = (b: any): string | null =>
  (Number(b?.unitsPerBox) > 0 && b?.boxQuantity != null)
    ? `${qf(b.boxQuantity)} box + ${qf(b.looseUnitQuantity)} loose`
    : (Number(b?.unitsPerBox) > 0 ? `${qf(b.unitsPerBox)} / box` : null);

function ShipmentItems({ r }: { r: any }) {
  const groups = groupByProduct(r.items ?? []);
  const batches = (r.items ?? []).length;
  return (
    <div>
      <ColHead>
        Items · {plural(groups.length, 'product')}
        {batches !== groups.length && <span className="text-muted"> · {plural(batches, 'batch', 'batches')}</span>}
      </ColHead>
      <div className="overflow-hidden rounded-control border border-line bg-surface">
        <table className="w-full text-sm">
          {/* FOUR COLUMNS. The batch has one of its own rather than being a second line under the
              product, so "which lot is this" is read down a column instead of found between names, and
              a product picked from one batch does not have to print its quantity twice to say so.
              Every number in the panel still shares one right edge. */}
          {/* The batch column holds a code chip AND its expiry on ONE line — 11.5rem fitted the chip
              alone and made the date wrap underneath it. */}
          <colgroup><col /><col className="w-[14.5rem]" /><col className="w-[7.5rem]" /><col className="w-16" /></colgroup>
          <thead className="gb-thead-tint">
            <tr className="border-b border-line text-left text-2xs uppercase tracking-wide text-subtle">
              <th className="px-3 py-1.5 font-medium">Product</th>
              <th className="px-3 py-1.5 font-medium">Batch</th>
              <th className="px-2 py-1.5 text-right font-medium">Pack</th>
              <th className="px-3 py-1.5 text-right font-medium">Qty</th>
            </tr>
          </thead>
          {groups.map((g, gi) => {
            // One batch is not a split — it is this line's batch, and it belongs on this line.
            const single = g.batches.length === 1 ? g.batches[0] : null;
            return (
            // One <tbody> per product: the grouping is in the MARKUP, so a screen reader and a
            // copy-paste into a spreadsheet both keep the product and its batches together.
            <tbody key={g.key} className="border-b border-line last:border-0">
              <tr className={single ? undefined : 'bg-surface-2/60'}>
                <td className="px-3 py-1.5">
                  <span className="flex min-w-0 items-center gap-2">
                    {/* The position number is what tells two rows apart when the names are near-identical
                        (AGN 0.5L / AGN 1L); the tinted initials carry the product's own colour. */}
                    <span className="type-caption tabular-nums text-subtle">{gi + 1}</span>
                    <span className="grid size-5 shrink-0 place-items-center rounded-[5px] text-[9px] font-semibold leading-none" style={hueSkin(accentForProductName(g.productName), 16, 42)}>
                      {initialsOf(g.productName)}
                    </span>
                    <span className="truncate font-medium text-content">{g.productName}</span>
                  </span>
                </td>
                <td className="px-3 py-1.5">
                  {single
                    ? <BatchCell b={single} />
                    : (
                      // The product row of a split says HOW MANY lots, and the rows beneath name them.
                      <span className="inline-flex items-center gap-1 rounded-pill bg-surface-3 px-1.5 py-px type-caption text-muted">
                        <Layers size={10} />{g.batches.length} batches
                      </span>
                    )}
                </td>
                {/*
                  A split's product row carries the pack SIZE — a property of the product, said once
                  rather than repeated on every batch beneath it. No box TOTAL is printed there: boxes
                  belong to the batch that was picked, and a total here would be a figure none of the
                  rows below supports. A single line has no rows below, so it carries its own make-up.
                */}
                <td className="whitespace-nowrap px-2 py-1.5 text-right type-caption text-subtle">
                  {single ? packText(single) : (Number(g.batches[0]?.unitsPerBox) > 0 ? `${qf(g.batches[0].unitsPerBox)} / box` : null)}
                </td>
                <td className="px-3 py-1.5 text-right font-semibold tabular-nums text-content">{qf(g.qty)}</td>
              </tr>
              {!single && g.batches.map((b: any, bi: number) => (
                <tr key={bi} className="border-t border-line/40">
                  <td className="px-3 py-1.5" />
                  {/* Indented under its product, and the indent is DRAWN — a hairline rail — so the
                      relationship survives even when the panel is narrow enough to wrap. */}
                  <td className="py-1.5 pl-3 pr-3">
                    <span className="flex min-w-0 items-center border-l border-line pl-2.5"><BatchCell b={b} /></span>
                  </td>
                  <td className="whitespace-nowrap px-2 py-1.5 text-right type-caption text-subtle">
                    {packText(b) ?? <span className="text-line-strong">—</span>}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-muted">{qf(b.qty)}</td>
                </tr>
              ))}
            </tbody>
            );
          })}
          {groups.length > 1 && (
            <tfoot>
              <tr className="border-t border-line">
                <td colSpan={3} className="px-3 py-1.5 type-overline text-subtle">Total</td>
                <td className="px-3 py-1.5 text-right font-semibold tabular-nums text-content">{qf(r.totalQty)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

// Compact VERTICAL status timeline — Packed → Dispatched → Delivered; reached stages are brand dots joined by a
// brand line, unreached are hollow. A cancelled shipment collapses to Packed → Cancelled (red).
function VerticalTimeline({ r }: { r: any }) {
  const cancelled = r.status === 'CANCELLED';
  const steps: { label: string; at: any; by?: string | null; danger?: boolean }[] = cancelled
    ? [{ label: 'Packed', at: r.packedAt, by: r.packedByName }, { label: 'Cancelled', at: null, danger: true }]
    : [
        { label: 'Packed', at: r.packedAt, by: r.packedByName },
        { label: 'Dispatched', at: r.dispatchedAt },
        { label: 'Delivered', at: r.deliveredAt },
      ];
  return (
    <ol className="relative">
      {steps.map((st, i) => {
        const done = !!st.at || !!st.danger;
        const last = i === steps.length - 1;
        const nextReached = !last && (!!steps[i + 1].at || !!steps[i + 1].danger);
        return (
          <li key={st.label} className="relative flex gap-2.5 pb-3.5 last:pb-0">
            {!last && <span aria-hidden className={`absolute left-1.25 top-4 h-full w-0.5 ${nextReached && !steps[i + 1].danger ? 'bg-brand-500' : 'bg-line'}`} />}
            <span className={`relative z-10 mt-1 size-3 shrink-0 rounded-full ${st.danger ? 'bg-danger-500' : done ? 'bg-brand-500' : 'border border-line bg-surface-3'}`} />
            <div className="min-w-0 text-sm leading-tight">
              <div className={`font-semibold ${st.danger ? 'text-danger-600' : done ? 'text-content' : 'text-subtle'}`}>{st.label}</div>
              <div className="mt-0.5 text-2xs text-muted">{st.at ? dt(st.at) : (st.danger ? (r.notes ?? '—') : 'Pending')}</div>
              {done && st.by && <div className="text-2xs text-subtle">{st.by}</div>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function ColHead({ children }: { children: React.ReactNode }) {
  return <div className="mb-2 type-overline text-subtle">{children}</div>;
}
// Key-value grid styled to match the app's antd Descriptions look — brand-green label cells + plain value cells,
// hairline-bordered (the `gap-px bg-line` trick draws the grid lines). Two label/value pairs per row. Uses the
// same brand-50 tint as Descriptions so detail blocks read consistently green (not the cool surface-2 grey).
function DetailGrid({ rows }: { rows: { label: string; value: any }[][] }) {
  const labelCls = 'flex items-center bg-brand-50 px-3 py-1.5 type-label text-muted dark:bg-brand-900/25';
  const valueCls = 'flex items-center bg-surface px-3 py-1.5 text-sm text-content';
  const fmt = (v: any) => (v == null || v === '' ? <span className="text-subtle">—</span> : v);
  return (
    <div className="overflow-hidden rounded-control border border-line">
      <div className="grid gap-px bg-line sm:grid-cols-[minmax(84px,auto)_minmax(0,1fr)_minmax(84px,auto)_minmax(0,1fr)]">
        {rows.map((pair, i) => (
          <Fragment key={i}>
            <div className={labelCls}>{pair[0].label}</div>
            <div className={valueCls}>{fmt(pair[0].value)}</div>
            {pair[1] ? (
              <>
                <div className={labelCls}>{pair[1].label}</div>
                <div className={valueCls}>{fmt(pair[1].value)}</div>
              </>
            ) : (
              <>
                <div className="hidden bg-brand-50 dark:bg-brand-900/25 sm:block" />
                <div className="hidden bg-surface sm:block" />
              </>
            )}
          </Fragment>
        ))}
      </div>
    </div>
  );
}
function DocChip({ icon, label, value, to, warn }: { icon: React.ReactNode; label: string; value: string; to?: string; warn?: string }) {
  const inner = (
    <span className="inline-flex items-center gap-1.5 rounded-control border border-line bg-surface px-3 py-1.5 text-sm">
      <span className="text-muted">{icon}</span>
      <span className="text-subtle">{label}</span>
      <span className="font-medium text-content">{value}</span>
      {warn ? <span className="text-warning-600">· {warn}</span> : null}
    </span>
  );
  return to ? <Link to={to} onClick={(e) => e.stopPropagation()} className="transition-opacity hover:opacity-80">{inner}</Link> : inner;
}
/** Thin wrapper over the shared StatCard — this page used to hand-roll its own tile, which is why
 *  its KPIs sat at a different size/padding from the rest of the app. `valueCls` is intentionally
 *  dropped: StatCard keeps the accent on the border + icon chip and the value in `text-content`,
 *  which is what makes a row of tiles read as one set instead of four coloured numbers. */
function Kpi({ title, value, accent, icon, hint, onClick, active }: {
  title: string; value: number; accent: string; valueCls?: string; icon?: React.ReactNode;
  hint?: React.ReactNode; onClick?: () => void; active?: boolean;
}) {
  return <StatCard label={title} value={value} tone={statTone(accent)} icon={icon} hint={hint} onClick={onClick} active={active} />;
}
